// DME Items & Charges — the fee schedule.
//
// One row per HCPCS code per payer: what it bills at, what the payer allows
// per period, and who is eligible by age. The order form lists items from
// here, the expected claim is rate × quantity from here, and the stage gates
// check quantity and age against it. Seeded with the Georgia Medicaid
// incontinence / ostomy / urological schedule; edit as rates change and add
// rows when a new item or payer comes on.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import DataTable from '../../components/DataTable';
import { StatTile } from '../../components/StatTile';
import { CATEGORIES, money } from '../../lib/dmeWorkflow';
import { PAYERS } from '../../lib/serviceModel';

const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit', boxSizing: 'border-box' };
const labelStyle = { fontSize: 11, fontWeight: 500, color: 'var(--text2)', marginBottom: 4, display: 'block' };
const FG = ({ label, children, half, hint }) => (
  <div style={{ flex: half ? '1 1 calc(50% - 6px)' : '1 1 100%', minWidth: 0 }}>
    <label style={labelStyle}>{label}</label>{children}
    {hint && <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 3 }}>{hint}</div>}
  </div>
);

function ModalBackdrop({ onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 14, width: '100%', maxWidth: 600, boxShadow: '0 24px 48px rgba(0,0,0,0.25)', flexShrink: 0 }}>{children}</div>
    </div>
  );
}

const blank = {
  hcpcs_code: '', description: '', category: 'incontinence', unit: 'each', payer_key: 'medicaid',
  rate: '', allowable_qty: '', allowable_period: 'month', min_age: '', max_age: '',
  cmn_required: false, pa_required_over_limit: true, is_active: true, notes: '',
};

export default function DmeItems() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [cat, setCat]           = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(blank);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('dme_fee_schedule').select('*').order('category').order('hcpcs_code');
    setItems(data || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => items.filter(i => {
    if (!showInactive && !i.is_active) return false;
    if (cat && i.category !== cat) return false;
    const q = search.trim().toLowerCase();
    return !q || i.hcpcs_code.toLowerCase().includes(q) || i.description.toLowerCase().includes(q);
  }), [items, showInactive, cat, search]);

  const counts = useMemo(() => ({
    total: items.filter(i => i.is_active).length,
    incontinence: items.filter(i => i.is_active && i.category === 'incontinence').length,
    ostomy: items.filter(i => i.is_active && i.category === 'ostomy').length,
    catheter: items.filter(i => i.is_active && i.category === 'catheter').length,
  }), [items]);

  const openNew  = () => { setEditing(null); setForm(blank); setError(''); };
  const openEdit = (i) => { setEditing(i); setForm({ ...blank, ...i, rate: i.rate ?? '', allowable_qty: i.allowable_qty ?? '', min_age: i.min_age ?? '', max_age: i.max_age ?? '', notes: i.notes || '' }); setError(''); };
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    const code = form.hcpcs_code.trim().toUpperCase();
    if (!/^[A-Z]\d{4}$/.test(code)) { setError('HCPCS code is one letter and four digits, e.g. T4529.'); return; }
    if (!form.description.trim()) { setError('Enter a description.'); return; }
    if (form.rate === '' || Number(form.rate) < 0) { setError('Enter the rate.'); return; }
    setSaving(true); setError('');
    const num = (v) => (v === '' || v === null ? null : Number(v));
    const payload = {
      hcpcs_code: code, description: form.description.trim(), category: form.category, unit: form.unit || 'each',
      payer_key: form.payer_key, rate: Number(form.rate), allowable_qty: num(form.allowable_qty), allowable_period: form.allowable_period,
      min_age: num(form.min_age), max_age: num(form.max_age), cmn_required: !!form.cmn_required,
      pa_required_over_limit: !!form.pa_required_over_limit, is_active: !!form.is_active, notes: form.notes || null,
      updated_at: new Date().toISOString(),
    };
    const { error: err } = editing
      ? await supabase.from('dme_fee_schedule').update(payload).eq('id', editing.id)
      : await supabase.from('dme_fee_schedule').insert({ ...payload, source: 'Added in CAERA' });
    setSaving(false);
    if (err) { setError(err.message.includes('duplicate') ? `${code} already exists for that payer.` : err.message); return; }
    setEditing(null); setForm(blank); setModalOpen(false); load();
  };

  const toggleActive = async (i) => {
    await supabase.from('dme_fee_schedule').update({ is_active: !i.is_active, updated_at: new Date().toISOString() }).eq('id', i.id);
    load();
  };

  const columns = [
    { accessorKey: 'hcpcs_code', header: 'HCPCS', size: 90, meta: { mono: true, mobile: 'primary' } },
    { accessorKey: 'description', header: 'Item', size: 320, meta: { mobile: 'primary' } },
    { accessorKey: 'category', header: 'Category', size: 110, meta: { mobile: 'secondary' },
      cell: info => <span style={{ textTransform: 'capitalize', fontSize: 11 }}>{info.getValue()}</span> },
    { accessorKey: 'payer_key', header: 'Payer', size: 90, meta: { mobile: 'hidden' },
      cell: info => PAYERS.find(p => p.key === info.getValue())?.short || info.getValue() },
    { accessorKey: 'rate', header: 'Rate', size: 80, meta: { align: 'right', mono: true, mobile: 'secondary' }, cell: info => money(info.getValue()) },
    { id: 'allowable', header: 'Allowable', size: 100, meta: { align: 'right', mobile: 'hidden' },
      accessorFn: r => r.allowable_qty ?? 0,
      cell: info => { const r = info.row.original; return r.allowable_qty ? `${r.allowable_qty}/${r.allowable_period}` : '—'; } },
    { id: 'ages', header: 'Ages', size: 70, meta: { mobile: 'hidden' }, enableSorting: false,
      cell: info => { const r = info.row.original; return r.min_age !== null || r.max_age !== null ? `${r.min_age ?? ''}–${r.max_age ?? ''}` : '—'; } },
    { accessorKey: 'cmn_required', header: 'CMN', size: 60, meta: { mobile: 'hidden' }, cell: info => info.getValue() ? 'Yes' : '—' },
    { accessorKey: 'is_active', header: 'Status', size: 80, meta: { mobile: 'secondary' },
      cell: info => <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: info.getValue() ? 'var(--success-bg)' : 'var(--bg2)', color: info.getValue() ? 'var(--success)' : 'var(--text3)' }}>{info.getValue() ? 'Active' : 'Inactive'}</span> },
    { id: 'actions', header: '', size: 140, enableSorting: false, meta: { mobile: 'secondary' },
      cell: info => { const i = info.row.original; return (
        <div style={{ display: 'flex', gap: 4 }}>
          <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={e => { e.stopPropagation(); openEdit(i); setModalOpen(true); }}>Edit</button>
          <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={e => { e.stopPropagation(); toggleActive(i); }}>{i.is_active ? 'Retire' : 'Restore'}</button>
        </div>); } },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>DME Items &amp; Charges</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>What can be ordered, what it bills at, and what each payer allows. Orders and stage checks read from here.</div>
        </div>
        <button className="btn btn-primary" onClick={() => { openNew(); setModalOpen(true); }}>+ Add Item</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 8, marginBottom: 14 }}>
        <StatTile label="Active items" value={counts.total} tone="neutral" compact />
        <StatTile label="Incontinence" value={counts.incontinence} tone="neutral" compact />
        <StatTile label="Ostomy" value={counts.ostomy} tone="neutral" compact />
        <StatTile label="Catheter" value={counts.catheter} tone="neutral" compact />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search code or description…" style={{ ...inputStyle, flex: '1 1 200px', maxWidth: 300 }} />
        <select value={cat} onChange={e => setCat(e.target.value)} style={{ ...inputStyle, width: 'auto', flex: '0 0 auto' }}>
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <label style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} /> Show retired
        </label>
      </div>

      <div className="card">
        <DataTable tableId="dme-items" columns={columns} data={filtered} getRowId={i => i.id} loading={loading}
          onRowClick={i => { openEdit(i); setModalOpen(true); }} emptyMessage="No items match." maxHeight="calc(100vh - 280px)" />
      </div>

      {modalOpen && (
        <ModalBackdrop onClose={() => setModalOpen(false)}>
          <div style={{ padding: '18px 24px', borderBottom: '0.5px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{editing ? `Edit ${editing.hcpcs_code}` : 'Add item'}</div>
            <button className="btn btn-outline btn-sm" onClick={() => setModalOpen(false)}>Cancel</button>
          </div>
          <div style={{ padding: '18px 24px', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <FG label="HCPCS code" half><input value={form.hcpcs_code} onChange={set('hcpcs_code')} style={inputStyle} placeholder="T4529" disabled={!!editing} /></FG>
            <FG label="Payer" half>
              <select value={form.payer_key} onChange={set('payer_key')} style={inputStyle}>
                {PAYERS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </FG>
            <FG label="Description"><input value={form.description} onChange={set('description')} style={inputStyle} /></FG>
            <FG label="Category" half>
              <select value={form.category} onChange={set('category')} style={inputStyle}>
                {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </FG>
            <FG label="Unit" half><input value={form.unit} onChange={set('unit')} style={inputStyle} placeholder="each" /></FG>
            <FG label="Rate per unit" half><input type="number" step="0.01" min="0" value={form.rate} onChange={set('rate')} style={inputStyle} /></FG>
            <FG label="Allowable quantity" half hint="Leave blank for no limit">
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="number" min="0" value={form.allowable_qty} onChange={set('allowable_qty')} style={inputStyle} />
                <select value={form.allowable_period} onChange={set('allowable_period')} style={{ ...inputStyle, width: 110 }}>
                  <option value="month">per month</option><option value="year">per year</option>
                </select>
              </div>
            </FG>
            <FG label="Minimum age" half hint="Blank = no minimum"><input type="number" min="0" value={form.min_age} onChange={set('min_age')} style={inputStyle} /></FG>
            <FG label="Maximum age" half hint="Georgia EPSDT incontinence: 20"><input type="number" min="0" value={form.max_age} onChange={set('max_age')} style={inputStyle} /></FG>
            <label style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', gap: 6, alignItems: 'center', flex: '1 1 calc(50% - 6px)' }}>
              <input type="checkbox" checked={!!form.cmn_required} onChange={set('cmn_required')} /> CMN required
            </label>
            <label style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', gap: 6, alignItems: 'center', flex: '1 1 calc(50% - 6px)' }}>
              <input type="checkbox" checked={!!form.is_active} onChange={set('is_active')} /> Active (offered on new orders)
            </label>
            <FG label="Notes"><textarea rows={2} value={form.notes} onChange={set('notes')} style={{ ...inputStyle, resize: 'vertical' }} /></FG>
            {error && <div style={{ fontSize: 12, color: 'var(--danger)', flex: '1 1 100%' }}>{error}</div>}
          </div>
          <div style={{ padding: '12px 24px', borderTop: '0.5px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-outline btn-sm" onClick={() => setModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={async () => { await save(); }}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Add item'}</button>
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}
